import { IonIcon } from "@ionic/react";
import { trashOutline } from "ionicons/icons";
import React, { useMemo } from "react";
import ArrowLeftToRight from "../../../image/icons/map/details/ArrowLeftToRight.svg?react";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { triggerSelectionHaptic } from "../../../tools/haptics.tools";

/** Ionic danger token used for destructive remove controls. */
const REMOVE_ACCENT_COLOR = "var(--ion-color-danger)";

interface ItineraryItemSavedHeaderProps {

    /** Whether dark overlay palette tokens should be used. */
    "isDarkUi": boolean;

    /** Itinerary id used for stable DOM ids. */
    "itineraryId": string;

    /** Departure place label (start of the itinerary). */
    "originLabel": string;

    /** Arrival place label (end of the itinerary). */
    "destinationLabel": string;

    /** Formatted itinerary departure date. */
    "displayDate": string;

    /** Removes the itinerary from the saved list. */
    "onRemove": () => void;
}

/**
 * Header band shown above a itinerary card in saved-itinerary list mode.
 *
 * @param props Header props.
 * @returns Saved-list card header with departure → arrival route summary.
 */
const ItineraryItemSavedHeader: React.FC<ItineraryItemSavedHeaderProps> = ( {
    isDarkUi,
    itineraryId,
    originLabel,
    destinationLabel,
    displayDate,
    onRemove,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const removeButtonStyle = useMemo( (): React.CSSProperties => ( {
        "borderColor": `color-mix(in srgb, ${ REMOVE_ACCENT_COLOR } 55%, ${ palette.overlayBorderStrong })`,
        "background":  isDarkUi ? palette.overlayCardBackgroundStrong : palette.overlayCardBackgroundSoft,
        "color":       REMOVE_ACCENT_COLOR,
    } ), [
        isDarkUi,
        palette.overlayBorderStrong,
        palette.overlayCardBackgroundSoft,
        palette.overlayCardBackgroundStrong,
    ] );
    const hasOrigin = originLabel.length > 0;
    const hasDestination = destinationLabel.length > 0;
    const routeAriaLabel = hasOrigin && hasDestination
        ? `${ originLabel } vers ${ destinationLabel }`
        : hasOrigin
            ? originLabel
            : hasDestination
                ? destinationLabel
                : "Itinéraire sauvegardé";

    /**
     * Stops card click propagation and removes the saved entry.
     *
     * @param event Pointer event from the remove control.
     */
    const handleRemoveClick = ( event: React.MouseEvent<HTMLButtonElement> ): void => {
        event.stopPropagation();
        void triggerSelectionHaptic();
        onRemove();
    };

    return (
        <div
            className="mb-2 border-b pb-2"
            id={`saved-itinerary-item-header-${ itineraryId }`}
            style={{ "borderBottomColor": palette.overlayBorder }}
        >
            <div className="flex items-center gap-2">
                <div
                    aria-label={routeAriaLabel}
                    className="min-w-0 flex-1"
                >
                    <div className="flex min-w-0 items-center gap-1.5">
                        {hasOrigin
                            ? (
                                <span
                                    className="truncate text-sm font-semibold"
                                    style={{ "color": palette.overlayTextPrimary }}
                                    title={originLabel}
                                >
                                    {originLabel}
                                </span>
                            )
                            : null}
                        {hasOrigin && hasDestination
                            ? (
                                <ArrowLeftToRight
                                    aria-hidden="true"
                                    className="h-3.5 w-3.5 shrink-0"
                                    style={{ "color": palette.overlayTextSecondary }}
                                />
                            )
                            : null}
                        {hasDestination
                            ? (
                                <span
                                    className="truncate text-sm font-semibold"
                                    style={{ "color": palette.overlayTextPrimary }}
                                    title={destinationLabel}
                                >
                                    {destinationLabel}
                                </span>
                            )
                            : null}
                        {!hasOrigin && !hasDestination
                            ? (
                                <span
                                    className="text-sm font-semibold"
                                    style={{ "color": palette.overlayTextPrimary }}
                                >
                                    Itinéraire sauvegardé
                                </span>
                            )
                            : null}
                    </div>
                    {displayDate.length > 0
                        ? (
                            <p
                                className="mt-0.5 text-xs"
                                style={{ "color": palette.overlayTextSecondary }}
                            >
                                {displayDate}
                            </p>
                        )
                        : null}
                </div>
                <button
                    aria-label="Retirer des itinéraires sauvegardés"
                    className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-solid backdrop-blur-sm transition-transform duration-150 ease-out active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                    id={`saved-itinerary-item-remove-${ itineraryId }`}
                    onClick={handleRemoveClick}
                    onPointerDown={( event ) => {
                        event.stopPropagation();
                    }}
                    style={removeButtonStyle}
                    type="button"
                >
                    <IonIcon
                        aria-hidden="true"
                        className="h-[16px] w-[16px]"
                        icon={trashOutline}
                    />
                </button>
            </div>
        </div>
    );
};

export default ItineraryItemSavedHeader;
